/**
 * src/components/features/renfest/boothChrome.jsx
 * ═══════════════════════════════════════════════════════════════════════════
 * Shared carved-wood chrome for the RenFest booth: the stylesheet and the top
 * bar. Both the Selfie view and the Shoppe view render inside it.
 *
 * ⚠️ THE STYLESHEET LIVES HERE, NOT IN tailwind.config.js. The fairground look
 * belongs to this booth and nowhere else in Shaman Intelligence — putting
 * carved wood into the global theme would be a permanent tax on every other
 * page. `MedievalSharp` is loaded in index.html alongside the app's other
 * fonts, and scoped here via `.rf-ink`.
 *
 * ⚠️ TAP-TARGET FLOOR IS 48px. Apple HIG says 44x44pt, Material says 48x48dp,
 * WCAG 2.2 asks 24px (44px AAA). 48 satisfies all of them and is the right call
 * for a sunlit field with a stranger's thumb on the glass. Inputs are 16px type
 * exactly, because iOS Safari auto-zooms the whole page below that and does not
 * zoom back out.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Camera, ScrollText, Feather } from 'lucide-react';
import { buzz } from './boothCues';
import { THEME } from '../../../lib/boothTheme';

/* ⚠️ THE ONE ACCENT COLOUR, AND IT FOLLOWS THE EVENT. Still exactly `#f0b429` on
   RenFest, where THEME is null. See src/lib/boothTheme.js. */
export const GOLD = THEME?.accent || '#f0b429';
export const BAND = { background: 'rgba(24,17,10,.93)', color: '#f6ecd8' };

/**
 * ⚠️ buzz() MOVED TO boothCues.jsx AND IS RE-EXPORTED HERE ON PURPOSE.
 *
 * It used to be a one-liner right here that swallowed every failure, which is
 * exactly why "we added vibration and it never showed up" went unexplained for
 * weeks — on iOS navigator.vibrate does not exist, the optional call returned
 * undefined, and nothing anywhere recorded that we had asked. The version in
 * boothCues counts what it asked for and what the device accepted, and that
 * count rides along in client_meta.
 *
 * Re-exported so the dozen existing `import { buzz } from './boothChrome'`
 * call sites keep working untouched. Read the platform notes at the top of
 * boothCues.jsx before changing any of this.
 */
export { buzz };

export function BoothStyles() {
  return (
    <>
    <style>{`
      @keyframes rf-sweep { 0% { transform: translateX(-120%) skewX(-12deg); } 100% { transform: translateX(320%) skewX(-12deg); } }
      @keyframes rf-halo  { 0%,100% { opacity:.55; } 50% { opacity:1; } }
      @keyframes rf-rise  { from { opacity:0; transform:translateY(6px);} to { opacity:1; transform:none; } }
      @keyframes rf-fade  { from { opacity:0; } to { opacity:.45; } }
      @keyframes rf-shimmer { 0%,100% { filter:brightness(1); } 50% { filter:brightness(1.14); } }
      .rf-sweep { animation: rf-sweep 2.6s ease-in-out infinite; }
      .rf-halo  { animation: rf-halo 1.6s ease-in-out infinite; }
      .rf-rise  { animation: rf-rise .5s ease-out both; }
      .rf-fade  { animation: rf-fade 1s ease-out both; }
      .rf-scroll { scrollbar-width: none; }
      .rf-scroll::-webkit-scrollbar { display: none; }

      .rf-ink { font-family: 'MedievalSharp', 'Papyrus', 'Copperplate', fantasy; }

      /* ⚠️ THE COSTUME COMES OFF FOR ANYTHING A GUEST READS CAREFULLY OR TYPES.
         MedievalSharp is charm on a headline and a liability on an email
         address: r-n reads as m, and l and 1 and I are near-identical. A guest
         checking whether we got their address right cannot do it in a face
         designed to look hand-lettered. Joel, on the first live test,
         2026-08-27: "a little hard to read w/ the font, might make it a more
         clear font for that user interaction stuff."

         The cost of illegibility here is not aesthetic. It is a bounced email,
         which is a lost guest AND a hit to the sending reputation that
         delivers everybody else's portrait. Use rf-plain on inputs, on any
         address or code shown back for checking, and on error text.

         ⚠️ NO BACKTICKS IN THIS BLOCK. These styles live inside a template
         literal, so a backtick here ends the string and the whole component
         stops parsing. That is exactly how this comment was first written and
         it failed the build immediately. */
      .rf-plain {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
                     'Helvetica Neue', Arial, sans-serif;
        font-variant-numeric: tabular-nums;
        letter-spacing: .01em;
      }

      /* ⚠️ The plank LIGHTENS again toward the bottom. A single dark-ward
         gradient plus a heavy inset bottom shadow made the lower third of a
         tall panel read as a hole rather than as wood — and the controls
         living down there went with it. Real timber under a fairground lamp
         catches light on its lower edge; so does this. */
      .rf-wood {
        background:
          repeating-linear-gradient(91deg, rgba(0,0,0,.06) 0 1px, transparent 1px 6px),
          repeating-linear-gradient(89deg, rgba(255,236,200,.04) 0 1px, transparent 1px 13px),
          radial-gradient(130% 70% at 18% -10%, rgba(255,214,150,.14), transparent 62%),
          radial-gradient(120% 55% at 50% 108%, rgba(255,205,140,.16), transparent 70%),
          linear-gradient(180deg, #744a26 0%, #5b3a1c 38%, #644120 76%, #7a5029 100%);
        border: 1px solid rgba(0,0,0,.45);
        border-radius: 1.5rem;
        box-shadow:
          inset 0 1px 0 rgba(255,228,182,.22),
          inset 0 -1px 0 rgba(255,222,170,.14),
          0 10px 28px rgba(0,0,0,.26);
      }

      .rf-field { display:flex; flex-wrap:wrap; align-items:center; gap:.55rem .85rem; padding:.5rem 0; }
      .rf-label {
        font-family:'MedievalSharp','Papyrus',fantasy;
        font-size:1.05rem; letter-spacing:.05em; color:#f7e4bb;
        text-shadow:0 1px 0 rgba(0,0,0,.65);
        min-width:6.5rem;
      }
      .rf-chips { display:flex; flex-wrap:wrap; gap:.45rem; }
      .rf-hint { font-size:.78rem; color:rgba(247,228,187,.7); width:100%; }
      .rf-chip {
        font-family:'MedievalSharp','Papyrus',fantasy;
        font-size:1.05rem; line-height:1;
        min-height:48px; padding:.6rem 1rem; border-radius:.8rem;
        color:#efdcba;
        background:linear-gradient(180deg,#7b5631,#54371b);
        border:1px solid rgba(0,0,0,.55);
        box-shadow: inset 0 1px 0 rgba(255,226,176,.22), 0 2px 0 rgba(0,0,0,.35);
        transition: transform .08s, filter .15s;
      }
      .rf-chip:active { transform: translateY(2px); box-shadow: inset 0 1px 3px rgba(0,0,0,.5); }
      .rf-chip:disabled { opacity:.45; }
      .rf-chip.is-on {
        color:#3a2408;
        background:linear-gradient(180deg,#f7cf74,#c98a22);
        border-color:#8a5c15;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.5), 0 0 16px rgba(240,180,41,.4), 0 2px 0 rgba(0,0,0,.4);
      }

      .rf-scene {
        border-radius:1rem; min-height:88px; padding:.6rem .25rem; text-align:center;
        background:linear-gradient(180deg,#7b5631,#4c3018);
        border:1px solid rgba(0,0,0,.55);
        box-shadow: inset 0 1px 0 rgba(255,226,176,.2), 0 2px 0 rgba(0,0,0,.35);
        transition: transform .08s;
      }
      .rf-scene:active { transform: translateY(2px); }
      .rf-scene.is-on {
        background:linear-gradient(180deg,#f7cf74,#c98a22);
        border-color:#8a5c15;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.5), 0 0 18px rgba(240,180,41,.45);
      }
      .rf-scene-name { font-family:'MedievalSharp','Papyrus',fantasy; font-size:.8rem; line-height:1.1; display:block; margin-top:.25rem; color:#efdcba; }
      .rf-scene.is-on .rf-scene-name { color:#3a2408; }

      .rf-big {
        font-family:'MedievalSharp','Papyrus',fantasy;
        min-height:60px;
        background:linear-gradient(180deg,#f7cf74,#c9891f);
        color:#2f1d06; border:1px solid #8a5c15;
        box-shadow: inset 0 2px 0 rgba(255,255,255,.55), 0 4px 0 rgba(0,0,0,.4), 0 8px 22px rgba(0,0,0,.35);
        transition: transform .08s;
      }
      .rf-big:active { transform: translateY(3px); box-shadow: inset 0 2px 6px rgba(0,0,0,.35); }

      .rf-input {
        font-family:'MedievalSharp','Papyrus',fantasy;
        font-size:16px;                      /* 16px exactly: iOS zooms below it */
        background:rgba(0,0,0,.3); color:#f7e4bb;
        border:1px solid rgba(0,0,0,.5); border-radius:.8rem;
        min-height:48px; padding:.65rem .8rem; width:100%;
        box-shadow: inset 0 2px 6px rgba(0,0,0,.4);
      }
      .rf-input::placeholder { color:rgba(247,228,187,.45); }

      .rf-home {
        font-family:'MedievalSharp','Papyrus',fantasy;
        display:inline-flex; align-items:center; gap:.45rem;
        min-height:48px; padding:.5rem 1rem; border-radius:.8rem;
        font-size:1.05rem; color:#efdcba;
        background:linear-gradient(180deg,#7b5631,#54371b);
        border:1px solid rgba(0,0,0,.55);
        box-shadow: inset 0 1px 0 rgba(255,226,176,.22), 0 2px 0 rgba(0,0,0,.35);
        transition: transform .08s;
      }
      .rf-home:active { transform: translateY(2px); }
      .rf-home.is-accent {
        color:#2f1d06;
        background:linear-gradient(180deg,#f7cf74,#c9891f);
        border-color:#8a5c15;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.5), 0 2px 0 rgba(0,0,0,.4), 0 0 18px rgba(240,180,41,.35);
      }

      /* ── the Shoppe ──────────────────────────────────────────────── */
      .rf-shelf {
        border-radius:1.1rem; overflow:hidden;
        background:linear-gradient(180deg, rgba(0,0,0,.34), rgba(0,0,0,.18));
        border:1px solid rgba(0,0,0,.5);
        box-shadow: inset 0 1px 0 rgba(255,226,176,.14), 0 3px 0 rgba(0,0,0,.3);
        transition: transform .1s, box-shadow .15s;
      }
      .rf-shelf:active { transform: translateY(2px); }
      .rf-shelf.is-in { box-shadow: inset 0 0 0 2px ${'#f0b429'}, 0 0 22px rgba(240,180,41,.35); }
      .rf-vessel-bed {
        background:
          radial-gradient(70% 60% at 50% 92%, rgba(0,0,0,.55), transparent 70%),
          linear-gradient(180deg, rgba(255,222,170,.10), rgba(0,0,0,.30));
      }
      .rf-tag {
        font-family:'MedievalSharp','Papyrus',fantasy;
        display:inline-block; padding:.15rem .55rem; border-radius:.4rem;
        background:linear-gradient(180deg,#f7cf74,#c9891f); color:#2f1d06;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.5), 0 1px 0 rgba(0,0,0,.4);
      }
      .rf-basket {
        position:sticky; bottom:0; z-index:5;
        background:rgba(24,17,10,.96); color:#f6ecd8;
        border-top:1px solid rgba(240,180,41,.35);
        box-shadow: 0 -10px 26px rgba(0,0,0,.45);
      }
    `}</style>
    {/* ⚠️ A SECOND SHEET, AFTER THE FIRST, AND ONLY FOR A THEMED EVENT. Every
        rule in it is scoped to the theme's root class, so on RenFest this
        renders nothing and the sheet above is the whole story. */}
    {THEME?.css ? <style>{THEME.css}</style> : null}
    </>
  );
}

/**
 * The booth's only navigation.
 *
 * ⚠️ TWO BUTTONS, NEVER MORE. On a device sitting on a fairground counter,
 * every extra exit is a way for a guest to end up somewhere they cannot get
 * back from — and while a portrait is generating, leaving the page kills the
 * poll and loses the picture. Home leaves the booth entirely; the second button
 * toggles between the two rooms and always shows where you are NOT.
 */
/**
 * ⚠️ THE FEATHER HERE IS lucide's, NOT THE BRAND MARK. TWO THINGS HAVE ALREADY
 * FAILED IN THIS SPOT — do not try a third variation of the same idea.
 *
 *  1. The full-colour brand feather scaled to 26px turned to mud. The intricate
 *     patterning that makes the mark beautiful on a shopfront is pure noise at
 *     icon size.
 *  2. A hand-drawn SVG silhouette of it read as a dark blob. Authoring icon
 *     geometry by writing path coordinates, without being able to look at the
 *     result, does not work — small-icon drawing is an eyes-on craft.
 *
 * lucide's `Feather` is professionally drawn, stroke-based, optically balanced
 * for 16-24px, and consistent with every other icon in this app. It reads
 * instantly as a feather, which is the entire job. The brand is carried by the
 * words next to it and by the gold; the icon only has to say "feather".
 *
 * If a bespoke mark is ever wanted here, it needs a designer and a real SVG
 * file, not another attempt at this.
 */

/**
 * ⚠️ THERE IS NO HOME BUTTON, AND THAT IS THE POINT.
 *
 * It navigated to `/` — a ROUTE CHANGE, which unmounts the selfie room and
 * kills the poll. A guest mid-portrait who tapped it lost the portrait. Joel,
 * 2026-08-27: "the Home Button needs to go. It just breaks the entire thing."
 *
 * This booth is reached by scanning a QR code. The guest did not come from a
 * homepage and has nowhere to go back to; the only thing "Home" could do was
 * take them out of the one screen they came for. Every other control in this
 * bar is a VIEW SWITCH, which is why the rest of them are safe.
 *
 * ⚠️ Do not add a route-changing control to this bar. If something genuinely
 * needs to leave the booth, it belongs after the portrait is delivered.
 */
export function BoothBar({ view, onView, isAdmin = false }) {
  const goingToShop = view === 'selfie';
  /* An event with no Shoppe has nothing for the right-hand button to toggle to.
     The Ledger button (admin only) is all that is left of the bar. */
  const hasShop = THEME?.shop !== false;
  return (
    <div className="flex items-center gap-2">
      {/* ⚠️ Presentation only. The server refuses the ledger's data to anyone
          who is not an admin — hiding the button is courtesy, not the gate. */}
      {isAdmin && (
        <button
          type="button"
          className={`rf-home${view === 'ledger' ? ' is-accent' : ''}`}
          onClick={() => { buzz(10); onView(view === 'ledger' ? 'selfie' : 'ledger'); }}
        >
          <ScrollText size={18} /> Ledger
        </button>
      )}

      {hasShop && (
      <button
        type="button"
        className="rf-home is-accent ml-auto"
        onClick={() => { buzz(10); onView(goingToShop ? 'shop' : 'selfie'); }}
      >
        {goingToShop ? (
          <>
            {/* The feather alone carries the brand here. A bag icon beside it
                was one mark too many in a button this size. */}
            <Feather size={19} strokeWidth={2.25} />
            {/* The full lockup does not fit beside Home and Ledger on a phone,
                and wrapping it to a second row costs ~55px of the viewfinder. */}
            <span className="hidden sm:inline">Ye Old Dispensary</span>
            <span className="sm:hidden">Shop</span>
          </>
        ) : (
          <>
            {/* ⚠️ AN OFFER, NOT A LABEL. Joel, 2026-09-05: "we need to work on
                that selfie button and have it be a better call to action. Like,
                try your free selfie."

                This said "Selfie", which describes a destination to somebody
                who already knows what is there. The people who need this button
                most are the ones who scanned a SHOP code and have no idea a
                free portrait exists, so the word that has to be in it is FREE.

                Two lengths, same promise: a phone in portrait cannot hold the
                long one beside the other controls, and wrapping to a second row
                costs viewfinder height that the camera step needs. */}
            <Camera size={18} />
            <span className="hidden sm:inline">Try your free RenFest selfie</span>
            <span className="sm:hidden">Free selfie</span>
          </>
        )}
      </button>
      )}
    </div>
  );
}

/**
 * ⚠️ THE GLOBAL PROGRESS BAR. IT IS NOT COSMETIC.
 *
 * The Shoppe exists so a guest has somewhere to be during the minute their
 * portrait paints. The moment they tap into it, the in-picture bar goes with
 * the selfie room (`display:none`) and every signal that anything is happening
 * disappears. A guest who cannot see progress assumes it is broken, and the fix
 * they reach for is a RELOAD — which is how a portrait actually gets lost.
 *
 * ⚠️ IT RENDERS AS A ROW IN THE PAGE'S FLEX COLUMN, NEVER AS AN OVERLAY.
 * An absolutely-positioned bar inside a container somebody else wrote the CSS
 * for is a bar that renders perfectly in reasoning and is clipped out of
 * existence on a phone — `R-31`, and it has already happened once. A `shrink-0`
 * row in the flow cannot be eaten, and it does not sit on top of the Shoppe's
 * last item either.
 *
 * ⚠️ THE PERCENTAGE IS NOT COMPUTED HERE. It arrives from RenFestSelfie, which
 * is the one place `ringPct` exists. Two surfaces showing the same number is
 * fine; two surfaces DISAGREEING is the thing that must be impossible, so there
 * is exactly one source and this only renders it. Joel, 2026-08-27: "It's okay
 * to have it in two spots, but having conflicting numbers is not okay."
 *
 * ⚠️ IT DOES NOT MOVE THE GUEST. When the portrait lands it changes what it
 * SAYS and waits to be tapped. Yanking a view out from under someone reading
 * the Shoppe is its own kind of broken — Joel's call, same day: "No yanking
 * them on done, leave them in the state they are in, but flag to them picture
 * is done."
 *
 * `progress` is null whenever there is nothing true to report, and this renders
 * nothing. The three phases it does report are `painting`, `done` and `failed`
 * — `failed` because a run that dies while the guest is in the Shoppe would
 * otherwise leave them there forever, watching nothing, told nothing.
 */
export function BoothProgress({ progress, onOpen }) {
  if (!progress) return null;

  const { phase, pct = 0, passes = 0, target = 3 } = progress;
  const done   = phase === 'done';
  const failed = phase === 'failed';

  const label = done
    ? 'Your portrait is ready'
    : failed
      ? 'Something went wrong'
      : passes
        ? `Painting · pass ${passes} of ${target}`
        : 'Your portrait is painting';

  /* A failed run has no honest percentage, so it shows none — an empty track is
     the truthful picture of a run that is not progressing. */
  const fill = failed ? 0 : done ? 1 : pct;
  const accent = failed ? '#f6a6a6' : GOLD;

  return (
    /* 56px floor, and the WHOLE bar is the target. ORIENTATION §2: 44 and 48
       both assume somebody sitting still indoors. */
    <button
      type="button"
      onClick={() => { buzz(10); onOpen(); }}
      className="w-full text-left rounded-2xl overflow-hidden"
      style={{
        ...BAND,
        minHeight: 56,
        border: `1px solid ${done ? 'rgba(240,180,41,.55)' : 'rgba(247,228,187,.18)'}`,
        boxShadow: done ? '0 0 22px rgba(240,180,41,.30)' : '0 8px 24px rgba(0,0,0,.35)',
      }}
    >
      <div className="px-4 pt-3 pb-2 flex items-baseline gap-3">
        <span className={`rf-plain text-sm min-w-0 flex-1 truncate${done ? ' rf-halo' : ''}`}
              style={{ opacity: done ? 1 : .85, color: done ? accent : undefined }}>
          {label}
        </span>
        <span className="rf-plain shrink-0" style={{ color: accent, fontSize: '1.05rem', fontWeight: 700 }}>
          {done ? 'Tap to see it' : failed ? 'Tap' : `${Math.round(pct * 100)}%`}
        </span>
      </div>
      <div style={{ height: 6, background: 'rgba(0,0,0,.45)' }}>
        <div style={{
          height: '100%', width: `${fill * 100}%`, background: accent,
          transition: 'width .4s linear',
          boxShadow: `0 0 10px ${done ? 'rgba(240,180,41,.9)' : 'rgba(255,214,120,.6)'}`,
        }} />
      </div>
    </button>
  );
}
